import Swiper from 'swiper';
import { Pagination, EffectFade } from 'swiper/modules';
import { vars } from '@/app/config/variables';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

const modalGallerySelector = '#modal-product-gallery';

export class ProductGallery {
	constructor(selector) {
		this.$root = selector;
		this.active = 0;
		this.breakpoint = vars.breakpoint.laptop;
		this.mode = null;
		this.stackScrollRaf = null;
		this.scrollSyncPaused = false;
		this.scrollUnlockTimer = null;
		this.thumbSlidesPerView = 5;
		this.thumbSpeed = 700;

		if (!this.$root) return;

		this.init();
	}

	init() {
		this.$swiper = this.$root.querySelector('.js-product-swiper');
		this.$main = this.$swiper.querySelector('.product-swiper-main');
		this.$thumb = this.$swiper.querySelector('.product-swiper-thumb');
		this.$pagination = this.$swiper.querySelector('.swiper-pagination');
		this.$modal = document.querySelector(modalGallerySelector);

		this.initSwipers();
		this.bindEvents();
		this.updateLayout(true);
	}

	initSwipers() {
		this.swiperMain = new Swiper(this.$main, {
			modules: [Pagination, EffectFade],
			slidesPerView: 1,
			spaceBetween: 0,
			rewind: true,
			effect: 'fade',
			fadeEffect: { crossFade: true },
			pagination: {
				el: this.$pagination,
				clickable: true,
			},
		});
	}

	initThumbSwiper() {
		if (this.swiperThumb) {
			this.updateThumbViewport();
			return;
		}

		this.swiperThumb = new Swiper(this.$thumb, {
			slidesPerView: 'auto',
			spaceBetween: this.getGalleryVar('--product-thumb-gap', 10),
			direction: 'vertical',
			speed: this.thumbSpeed,
			allowTouchMove: true,
			simulateTouch: true,
		});

		this.updateThumbViewport();
	}

	updateLayout(force = false) {
		const isStack = window.innerWidth >= this.breakpoint;
		const nextMode = isStack ? 'stack' : 'slider';

		if (!force && nextMode === this.mode) return;

		this.mode = nextMode;
		this.$swiper.classList.toggle('product-swiper--stack', isStack);

		if (isStack) {
			this.enableStackMode();
		} else {
			this.enableSliderMode();
		}
	}

	enableStackMode() {
		this.initThumbSwiper();
		this.swiperMain.allowTouchMove = false;
		this.disconnectStackScrollSync();

		requestAnimationFrame(() => {
			this.updateThumbViewport();
		});

		this.initStackScrollSync();
	}

	enableSliderMode() {
		this.swiperMain.allowTouchMove = true;
		this.disconnectStackScrollSync();

		const index = this.swiperThumb?.activeIndex ?? this.swiperMain.activeIndex;

		this.swiperMain.slideTo(index, 0);
	}

	slideThumbTo(index, options = {}) {
		if (!this.swiperThumb || index < 0) return;

		const { speed = this.thumbSpeed, reveal = false } = options;
		const swiper = this.swiperThumb;
		const { firstVisible, lastVisible, visibleCount } = this.getThumbVisibleRange();
		const maxStart = Math.max(0, swiper.slides.length - visibleCount);
		let scrollTo = null;

		if (index < firstVisible) {
			scrollTo = index;
		} else if (index > lastVisible) {
			scrollTo = Math.min(maxStart, index - visibleCount + 1);
		} else if (reveal && firstVisible > 0 && index === firstVisible) {
			scrollTo = firstVisible - 1;
		} else if (reveal && lastVisible < swiper.slides.length - 1 && index === lastVisible) {
			scrollTo = Math.min(maxStart, firstVisible + 1);
		}

		if (scrollTo !== null && scrollTo !== firstVisible) {
			swiper.slideTo(scrollTo, speed);
		}

		this.active = index;
		this.setThumbActive(index);
	}

	bindEvents() {
		this.onResize = this.onResize.bind(this);
		window.addEventListener('resize', this.onResize);

		this.openHandler = this.openHandler.bind(this);
		this.$main.addEventListener('click', this.openHandler);

		this.changeHandler = this.changeHandler.bind(this);
		this.swiperMain.on('slideChange', this.changeHandler);

		this.thumbClickHandler = this.thumbClickHandler.bind(this);
		this.$thumb.addEventListener('click', this.thumbClickHandler);

		this.modalCloseHandler = this.modalCloseHandler.bind(this);
		this.$modal?.addEventListener('modalClose', this.modalCloseHandler);
	}

	onResize() {
		const wasStack = this.mode === 'stack';

		this.updateLayout();

		if (wasStack || this.mode === 'stack') {
			this.updateThumbViewport();
		}
	}

	changeHandler(swiper) {
		if (this.mode !== 'slider') return;

		this.active = swiper.activeIndex;
		this.checkVideo(swiper.previousIndex, swiper.activeIndex);
	}

	modalCloseHandler() {
		const media = window.app.modalGallery?.getSlider(modalGallerySelector);
		const index = media?.getActiveIndex() ?? this.active;

		if (this.mode === 'stack') {
			this.pauseScrollSync();
			this.swiperMain.slides[index]?.scrollIntoView({ block: 'start' });
			this.slideThumbTo(index, { speed: 0 });
			this.resumeScrollSync(100);
			return;
		}

		this.swiperMain.slideTo(index, 0);
	}

	thumbClickHandler(event) {
		if (this.mode !== 'stack') return;

		const $thumb = event.target.closest('.swiper-slide');

		if (!$thumb) return;

		const index = this.swiperThumb.slides.indexOf($thumb);

		if (index < 0) return;

		this.pauseScrollSync();
		this.slideThumbTo(index, { reveal: true });
		this.swiperMain.slides[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		this.unlockScrollSyncOnScroll();
	}

	openHandler(event) {
		if (this.mode === 'stack') {
			const $slide = event.target.closest('.swiper-slide');

			if (!$slide || $slide.classList.contains('swiper-slide-video')) return;

			const index = Array.from(this.swiperMain.slides).indexOf($slide);

			window.app.modalGallery?.open(modalGallerySelector, index);
			return;
		}

		const target = this.swiperMain.slides[this.swiperMain.activeIndex];

		if (target?.classList.contains('swiper-slide-video')) return;

		window.app.modalGallery?.open(modalGallerySelector, this.swiperMain.activeIndex);
	}

	getGalleryVar(name, fallback) {
		return Number.parseFloat(getComputedStyle(this.$root).getPropertyValue(name)) || fallback;
	}

	updateThumbViewport() {
		if (!this.swiperThumb?.slides[0]) return;

		const slideHeight = this.swiperThumb.slides[0].offsetHeight;
		const gap = this.swiperThumb.params.spaceBetween;
		const viewportHeight = this.thumbSlidesPerView * slideHeight + (this.thumbSlidesPerView - 1) * gap;
		const maxHeight = Number.parseFloat(getComputedStyle(this.$thumb).maxHeight);
		const height = Number.isFinite(maxHeight) ? Math.min(viewportHeight, maxHeight) : viewportHeight;

		this.$thumb.style.height = `${height}px`;
		this.reflowThumbSwiper();

		requestAnimationFrame(() => {
			this.slideThumbTo(this.active, { speed: 0 });
		});
	}

	reflowThumbSwiper() {
		if (!this.swiperThumb) return;

		this.$thumb.style.transform = 'translateZ(0)';
		void this.$thumb.offsetHeight;
		this.swiperThumb.updateSize();
		this.swiperThumb.update();
	}

	getThumbVisibleRange() {
		const swiper = this.swiperThumb;
		const slideHeight = swiper?.slides[0]?.offsetHeight || 0;
		const gap = swiper?.params.spaceBetween || 0;
		const stride = slideHeight + gap;

		if (!swiper || !stride) {
			return { firstVisible: 0, lastVisible: 0, visibleCount: this.thumbSlidesPerView };
		}

		const firstVisible = Math.min(Math.max(0, swiper.snapIndex ?? Math.floor(-swiper.translate / stride)), swiper.slides.length - 1);
		const visibleCount = Math.min(swiper.slides.length, Math.max(1, Math.floor((swiper.el.offsetHeight + gap) / stride)));
		const lastVisible = Math.min(swiper.slides.length - 1, firstVisible + visibleCount - 1);

		return { firstVisible, lastVisible, visibleCount };
	}

	getActiveMainIndex() {
		const anchor = this.getGalleryVar('--product-gallery-sticky-top', 50);
		const slides = this.swiperMain.slides;

		if (!slides.length || slides[0].getBoundingClientRect().top > anchor) {
			return 0;
		}

		for (let i = 0; i < slides.length; i++) {
			const { top, bottom } = slides[i].getBoundingClientRect();

			if (top <= anchor && bottom > anchor) {
				return i;
			}
		}

		for (let i = slides.length - 1; i >= 0; i--) {
			if (slides[i].getBoundingClientRect().top <= anchor) {
				return i;
			}
		}

		return 0;
	}

	setThumbActive(index) {
		this.swiperThumb?.slides.forEach(($slide, slideIndex) => {
			$slide.classList.toggle('swiper-slide-thumb-active', slideIndex === index);
		});
	}

	pauseScrollSync() {
		this.scrollSyncPaused = true;
		this.clearScrollUnlock();
	}

	resumeScrollSync(delay = 0) {
		clearTimeout(this.scrollUnlockTimer);

		if (!delay) {
			this.scrollSyncPaused = false;
			return;
		}

		this.scrollUnlockTimer = setTimeout(() => {
			this.scrollSyncPaused = false;
			this.scrollUnlockTimer = null;
			window.removeEventListener('scroll', this.onScrollUnlock);
		}, delay);
	}

	clearScrollUnlock() {
		clearTimeout(this.scrollUnlockTimer);
		this.scrollUnlockTimer = null;
		window.removeEventListener('scroll', this.onScrollUnlock);
	}

	unlockScrollSyncOnScroll() {
		this.onScrollUnlock = this.onScrollUnlock || (() => this.resumeScrollSync(150));

		window.removeEventListener('scroll', this.onScrollUnlock);
		window.addEventListener('scroll', this.onScrollUnlock, { passive: true });
		this.resumeScrollSync(1200);
	}

	initStackScrollSync() {
		this.onStackScroll =
			this.onStackScroll ||
			(() => {
				if (this.scrollSyncPaused || this.mode !== 'stack' || this.stackScrollRaf) return;

				this.stackScrollRaf = requestAnimationFrame(() => {
					this.stackScrollRaf = null;

					const index = this.getActiveMainIndex();

					if (index !== this.active) {
						this.slideThumbTo(index);
					}
				});
			});

		window.removeEventListener('scroll', this.onStackScroll);
		window.addEventListener('scroll', this.onStackScroll, { passive: true });

		const index = this.getActiveMainIndex();

		if (index !== this.active) {
			this.slideThumbTo(index, { speed: 0 });
		}
	}

	disconnectStackScrollSync() {
		window.removeEventListener('scroll', this.onStackScroll);

		if (this.stackScrollRaf) {
			cancelAnimationFrame(this.stackScrollRaf);
			this.stackScrollRaf = null;
		}
	}

	checkVideo(previousIndex, activeIndex) {
		this.swiperMain.slides.forEach(($slide) => {
			$slide.querySelector('video')?.pause();
		});

		if (activeIndex === previousIndex) return;

		this.swiperMain.slides[activeIndex]
			?.querySelector('video')
			?.play()
			.catch(() => {});
	}

	destroy() {
		this.clearScrollUnlock();
		this.disconnectStackScrollSync();
		window.removeEventListener('resize', this.onResize);
		this.$modal?.removeEventListener('modalClose', this.modalCloseHandler);
		this.$main?.removeEventListener('click', this.openHandler);
		this.$thumb?.removeEventListener('click', this.thumbClickHandler);
		this.swiperMain?.destroy(true, true);
		this.swiperThumb?.destroy(true, true);
	}
}
